import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiService, GetMyRequests, Template } from "../../api";
import { Space } from "antd";
import { useNavigate } from "react-router-dom";
import DynamicFilterForm, {
  FilterValues,
} from "../../components/DynamicFilterForm";
import { exportRequestsToExcel } from "../../utils/exportRequests";
import { usePagination } from "../../hooks/usePagination";
import ManageableTable, {
  ManageableColumn,
} from "../../components/ManageableTable";
import { useRefreshOnTabFocus } from "../../tabs/tabContext";
import { PageHeader, useHeaderSlot } from "../../components/PageHeader";

const MyRequestsListPage = () => {
  const { t } = useTranslation();
  const [requests, setRequests] = useState<GetMyRequests[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [reload, setReload] = useState<number>(0);
  const [filterValues, setFilterValues] = useState<FilterValues>({});
  const [templates, setTemplates] = useState<any[]>([]);
  const [statuses, setStatuses] = useState<any[]>([]);
  const { page, pageSize, total, applyResponse, resetPage, paginationProps } =
    usePagination();
  const { slot, target } = useHeaderSlot();
  const filtered = Object.keys(filterValues).length > 0;
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      const [tpls, sts] = await Promise.all([
        ApiService.apiMainTemplatesList(),
        ApiService.apiMainRequestStatusesList(),
      ]);
      setTemplates(tpls);
      setStatuses(sts);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const result = await ApiService.apiMainMyRequestsList({
          ...filterValues,
          page,
          page_size: pageSize,
        });
        setRequests(applyResponse<GetMyRequests>(result as any));
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValues, page, pageSize, reload]);

  // A permit created or edited in another tab would otherwise be missing from
  // this list until a full browser reload.
  useRefreshOnTabFocus(() => setReload((n) => n + 1));

  const columns: ManageableColumn<GetMyRequests>[] = [
    {
      key: "number",
      title: t("Number"),
      dataIndex: "number",
      width: 160,
    },
    {
      key: "description",
      title: t("Description"),
      dataIndex: "description",
      width: 280,
    },
    {
      key: "template",
      title: t("Template"),
      dataIndex: "template",
      width: 160,
      render: (template: Template) => template.name,
    },
    {
      key: "status",
      title: t("Status"),
      dataIndex: ["status", "name"],
      width: 140,
    },
    {
      key: "actions",
      title: t("Actions"),
      width: 160,
      hideable: false,
      render: (_: any, request: GetMyRequests) => (
        <Space>
          <Space size="middle">
            <a onClick={() => navigate(`/requests/${request.id}`)}>
              {t("View")}
            </a>
          </Space>
          <Space size="middle">
            <a
              onClick={() =>
                navigate(`/requests/${request.template.short_name}/create`, {
                  state: { requestId: request.id },
                })
              }
            >
              {t("Duplicate")}
            </a>
          </Space>
        </Space>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t("My Requests")}
        meta={
          filtered
            ? t("{{count}} matching the filter", { count: total })
            : t("{{count}} in total", { count: total })
        }
        actions={slot}
      />
      <DynamicFilterForm
        templates={templates}
        statuses={statuses}
        onFilter={(values) => {
          setFilterValues(values);
          resetPage();
        }}
        onExport={() =>
          exportRequestsToExcel(
            { scope: "my", filter: filterValues },
            "my_requests.xlsx"
          )
        }
        showDescription
        showTemplate
      />
      <ManageableTable
        tableId="requests-mine"
        toolbarTarget={target}
        dataSource={requests}
        columns={columns}
        bordered
        loading={loading}
        pagination={paginationProps}
        rowKey={(r: any) => r.id}
      />
    </>
  );
};

export default MyRequestsListPage;
